import { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Spinner,
  Chip,
  AlertDialog,
} from "@heroui/react";
import { Zap, ShieldCheck, Sparkles, Sliders } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  toggleCampaignRule,
  type CampaignRule,
  type CampaignProblem,
} from "@/entities/campaign";

export interface OptimizationModulesProps {
  campaignId?: string | number;
  rules?: CampaignRule[];
  problems?: CampaignProblem[];
}

interface FallbackModule {
  id: number | string;
  name: string;
  description: string;
  action_type: string;
  is_enabled: boolean;
}

const FALLBACK_MODULES: FallbackModule[] = [
  {
    id: 1,
    name: "Очистка площадок РСЯ",
    description:
      "Автоматически выявляет и отключает неэффективные и мусорные площадки в сетях, оптимизируя расход бюджета.",
    action_type: "EXCLUDE_PLACEMENT",
    is_enabled: false,
  },
  {
    id: "autobidder",
    name: "Автобиддер ставок",
    description:
      "Автоматическая корректировка ставок в аукционе для удержания целевой позиции и снижения цены клика.",
    action_type: "BID_ADJUSTMENT",
    is_enabled: true,
  },
  {
    id: "keywords",
    name: "Автогенерация минус-фраз",
    description:
      "Анализ поисковых запросов и автоматическое добавление нерелевантных слов в минус-фразы кампании.",
    action_type: "NEGATIVE_KEYWORDS",
    is_enabled: false,
  },
];

const getModuleIcon = (actionType: string, name: string) => {
  const lower = `${actionType} ${name}`.toLowerCase();
  if (
    actionType === "EXCLUDE_PLACEMENT" ||
    lower.includes("рся") ||
    lower.includes("площад")
  ) {
    return ShieldCheck;
  }
  if (lower.includes("ставк") || lower.includes("биддер")) {
    return Zap;
  }
  if (lower.includes("минус") || lower.includes("фраз")) {
    return Sparkles;
  }
  return Sliders;
};

export const OptimizationModules = ({
  campaignId,
  rules,
  problems,
}: OptimizationModulesProps) => {
  const queryClient = useQueryClient();
  const [pendingRuleId, setPendingRuleId] = useState<number | string | null>(
    null,
  );

  // Отрисовываем доступные модули из ответа кампании, если они переданы
  const modulesList = rules && rules.length > 0 ? rules : FALLBACK_MODULES;

  const toggleMutation = useMutation({
    mutationFn: async ({
      ruleId,
      targetStatus,
    }: {
      ruleId: number | string;
      targetStatus: "ENABLED" | "DISABLED";
    }) => {
      if (campaignId && typeof ruleId === "number") {
        await toggleCampaignRule(campaignId, ruleId, targetStatus);
      }
    },
    onSuccess: () => {
      if (campaignId) {
        queryClient.invalidateQueries({
          queryKey: ["campaign", String(campaignId)],
        });
        queryClient.invalidateQueries({ queryKey: ["campaign-actions"] });
        queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        queryClient.invalidateQueries({ queryKey: ["campaigns-problems"] });
      }
    },
    onSettled: () => {
      setPendingRuleId(null);
    },
  });

  const handleConnect = (module: CampaignRule | FallbackModule) => {
    setPendingRuleId(module.id);
    toggleMutation.mutate({ ruleId: module.id, targetStatus: "ENABLED" });
  };

  const handleConfirmDisconnect = (module: CampaignRule | FallbackModule) => {
    setPendingRuleId(module.id);
    toggleMutation.mutate({ ruleId: module.id, targetStatus: "DISABLED" });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs text-neutral-400">Модули оптимизации</div>
      <div className="flex flex-col gap-2">
        {modulesList.map((module) => {
          const Icon = getModuleIcon(module.action_type, module.name);
          const isPending = pendingRuleId === module.id;
          const isConnected = module.is_enabled;

          // Проверяем, связана ли с данным модулем нерешённая проблема
          const problem = problems?.find(
            (p) =>
              (typeof module.id === "number" && p.module_id === module.id) ||
              p.module_name.toLowerCase() === module.name.toLowerCase() ||
              (module.action_type === "EXCLUDE_PLACEMENT" &&
                p.module_code === "RSYA_CLEANUP"),
          );
          const isRecommended = Boolean(problem && !isConnected);

          const rule = module as CampaignRule;
          const startDate = rule.connected_at
            ? new Date(rule.connected_at).toLocaleDateString("ru-RU", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : null;

          let statusText = "Модуль работает";
          if (rule.work_status === "PENDING" || !rule.work_status) {
            statusText = "Ожидает исполнения";
          } else if (rule.work_status === "FAILED") {
            statusText = "Ошибка выполнения";
          } else if (rule.work_status === "HAS_RESULT") {
            statusText =
              (rule.entities_affected || 0) > 0
                ? `Модуль работает (отключено площадок: ${rule.entities_affected})`
                : "Модуль работает";
          }

          return (
            <Card
              key={module.id}
              className={isRecommended ? "border-2 border-accent" : ""}
            >
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <Icon className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <CardTitle className="text-sm font-semibold truncate">
                        {module.name}
                      </CardTitle>
                      {isRecommended && (
                        <Chip size="sm" variant="primary" color="accent">
                          Рекомендуется
                        </Chip>
                      )}
                    </div>
                    <CardDescription className="text-xs mt-0.5">
                      {module.description ||
                        "Модуль автоматической оптимизации рекламной кампании."}
                    </CardDescription>
                    {isConnected && (
                      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-neutral-400 mt-1">
                        {startDate ? (
                          <span>
                            Подключен: {startDate} • {statusText}
                          </span>
                        ) : (
                          <span>{statusText}</span>
                        )}
                      </div>
                    )}
                    {isRecommended && problem && (
                      <div className="text-xs text-accent mt-1">
                        Рекомендуется для решения проблемы: выявлено{" "}
                        {problem.entities_count} неэффективных площадок
                      </div>
                    )}
                  </div>
                </div>

                {isConnected ? (
                  <AlertDialog>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 min-w-[105px] hover:text-rose-500 hover:border-rose-500/50"
                      isDisabled={isPending}
                    >
                      {isPending ? <Spinner size="sm" /> : "Отключить"}
                    </Button>
                    <AlertDialog.Backdrop>
                      <AlertDialog.Container>
                        <AlertDialog.Dialog className="sm:max-w-[400px]">
                          <AlertDialog.CloseTrigger />
                          <AlertDialog.Header>
                            <AlertDialog.Icon status="danger" />
                            <AlertDialog.Heading>
                              Отключить модуль?
                            </AlertDialog.Heading>
                          </AlertDialog.Header>
                          <AlertDialog.Body>
                            <p>
                              Вы действительно хотите отключить модуль <strong>{module.name}</strong>? Автоматическая оптимизация и очистка площадок будут приостановлены.
                            </p>
                          </AlertDialog.Body>
                          <AlertDialog.Footer>
                            <Button slot="close" variant="tertiary">
                              Отмена
                            </Button>
                            <Button
                              slot="close"
                              variant="danger"
                              onClick={() => handleConfirmDisconnect(module)}
                            >
                              Отключить
                            </Button>
                          </AlertDialog.Footer>
                        </AlertDialog.Dialog>
                      </AlertDialog.Container>
                    </AlertDialog.Backdrop>
                  </AlertDialog>
                ) : (
                  <Button
                    size="sm"
                    variant="primary"
                    className="shrink-0 min-w-[105px]"
                    isDisabled={isPending}
                    onClick={() => handleConnect(module)}
                  >
                    {isPending ? <Spinner size="sm" /> : "Подключить"}
                  </Button>
                )}
              </CardHeader>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
